import { useState } from "react";
import axios from "axios";
import { Loader2, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COUNTRIES = ["Brasil", "Portugal", "Angola", "Moçambique", "Estados Unidos", "Argentina", "Paraguai", "Uruguai", "Chile", "Outro"];

/** Country / state / city (required) with CEP autofill via /api/cep/{cep}. `form` holds country,state,city,cep. */
export default function LocationFields({ form, setForm, errors = {}, onCepFilled, idPrefix = "register" }) {
  const [cepStatus, setCepStatus] = useState("idle"); // idle | loading | ok | error
  const [cepMsg, setCepMsg] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const lookupCep = async (raw) => {
    const digits = (raw || "").replace(/\D/g, "");
    set("cep", raw);
    if (digits.length !== 8) { setCepStatus("idle"); setCepMsg(""); return; }
    setCepStatus("loading"); setCepMsg("");
    try {
      const r = await axios.get(`${API}/cep/${digits}`);
      const d = r.data;
      setForm((f) => ({ ...f, country: "Brasil", state: d.state || f.state, city: d.city || f.city, cep: raw, street: d.street || f.street || "", neighborhood: d.neighborhood || f.neighborhood || "" }));
      setCepStatus("ok"); setCepMsg(`${d.city} - ${d.uf}${d.street ? ` · ${d.street}` : ""}`);
      onCepFilled?.(d);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setCepStatus("error"); setCepMsg(typeof detail === "string" && detail.length < 80 ? detail : "Não foi possível consultar o CEP agora. Preencha estado e cidade manualmente.");
    }
  };

  const err = (k) => errors[k] && <p data-testid={`${idPrefix}-${k}-error`} className="text-xs text-red-500 pl-1">{errors[k]}</p>;
  const cls = (k) => (errors[k] ? "border-red-500 focus-visible:ring-red-500" : "");

  return (
    <div className="space-y-2" data-testid={`${idPrefix}-location-block`}>
      <div className="relative">
        <Input
          data-testid={`${idPrefix}-cep`}
          placeholder="CEP (preenche estado e cidade automaticamente)"
          inputMode="numeric"
          value={form.cep || ""}
          onChange={(e) => lookupCep(e.target.value)}
          maxLength={9}
        />
        {cepStatus === "loading" && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-3 text-muted-foreground" />}
      </div>
      {cepMsg && (
        <p data-testid={`${idPrefix}-cep-msg`} className={`text-[11px] pl-1 inline-flex items-center gap-1 ${cepStatus === "error" ? "text-red-500" : "text-emerald-600"}`}>
          <MapPin className="w-3 h-3" /> {cepMsg}
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div className="space-y-1">
          <select
            data-testid={`${idPrefix}-country`}
            value={COUNTRIES.includes(form.country) || !form.country ? form.country || "" : "Outro"}
            onChange={(e) => set("country", e.target.value === "Outro" ? "" : e.target.value)}
            className={`h-10 w-full rounded-md border bg-background px-3 text-sm ${errors.country ? "border-red-500" : "border-input"}`}
          >
            <option value="">País *</option>
            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {err("country")}
        </div>
        <div className="space-y-1">
          <Input data-testid={`${idPrefix}-state`} placeholder="Estado *" value={form.state || ""} onChange={(e) => set("state", e.target.value)} className={cls("state")} />
          {err("state")}
        </div>
        <div className="space-y-1">
          <Input data-testid={`${idPrefix}-city`} placeholder="Cidade *" value={form.city || ""} onChange={(e) => set("city", e.target.value)} className={cls("city")} />
          {err("city")}
        </div>
      </div>
    </div>
  );
}
